import { POI, POICategory, ViewerEvents, Coordinates, ClusterPOI, POIOrCluster } from '../types';
import { EventEmitter } from '../utils/EventEmitter';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { poiLayerManager, POILayerGroup } from './POILayerManager';

/**
 * Service for managing Points of Interest (POIs)
 */
export class POIService extends EventEmitter<ViewerEvents> {
  private static instance: POIService;
  private pois: Map<string, POI> = new Map();
  private coordinateTransformer: CoordinateTransformer;
  private modelOrigin: Coordinates = { x: 0, y: 0, z: 0 };
  private clusteringEnabled: boolean = true; // Enabled by default for performance

  private constructor() {
    super();
    this.coordinateTransformer = CoordinateTransformer.getInstance();

    // Listen to layer manager events for POI visibility updates
    poiLayerManager.on('layer-visibility-changed', ({ affectedPOIs, visible }) => {
      this.updatePOIsVisibility(affectedPOIs, visible);
    });

    poiLayerManager.on('all-layers-visibility-changed', ({ visible }) => {
      this.updateAllPOIsVisibility(visible);
    });
  }

  /**
   * Set the model origin for coordinate transformations
   */
  setModelOrigin(origin: Coordinates): void {
    this.modelOrigin = origin;
  }

  /**
   * Add a new POI
   */
  addPOI(poi: POI): void {
    // Ensure coordinates are properly transformed
    const transformedPOI = this.ensureCoordinateConsistency(poi);

    this.pois.set(poi.id, transformedPOI);
    this.emit('poi-added', transformedPOI);
  }

  /**
   * Update an existing POI
   */
  updatePOI(poi: POI): void {
    if (!this.pois.has(poi.id)) {
      throw new Error(`POI with id ${poi.id} not found`);
    }

    const transformedPOI = this.ensureCoordinateConsistency(poi);
    this.pois.set(poi.id, transformedPOI);
    this.emit('poi-updated', transformedPOI);
  }

  /**
   * Remove a POI
   */
  removePOI(poiId: string): void {
    if (this.pois.has(poiId)) {
      this.pois.delete(poiId);
      // Could emit a 'poi-removed' event if needed
    }
  }

  /**
   * Get a POI by ID
   */
  getPOI(poiId: string): POI | undefined {
    return this.pois.get(poiId);
  }

  /**
   * Get all POIs
   */
  getAllPOIs(): POI[] {
    return Array.from(this.pois.values());
  }

  /**
   * Get POIs by category
   */
  getPOIsByCategory(category: POICategory): POI[] {
    return Array.from(this.pois.values()).filter(poi => poi.category === category);
  }

  /**
   * Get POIs within a geographic area
   */
  getPOIsInArea(bounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  }): POI[] {
    return Array.from(this.pois.values()).filter(poi => {
      const coords = poi.coordinates;
      return coords.x >= bounds.minX &&
             coords.x <= bounds.maxX &&
             coords.y >= bounds.minY &&
             coords.y <= bounds.maxY;
    });
  }

  /**
   * Search POIs by name, description, or properties
   */
  searchPOIs(query: string): POI[] {
    const lowerQuery = query.toLowerCase();

    return Array.from(this.pois.values()).filter(poi => {
      return poi.name.toLowerCase().includes(lowerQuery) ||
             poi.properties.description?.toLowerCase().includes(lowerQuery) ||
             poi.properties.type.toLowerCase().includes(lowerQuery) ||
             poi.properties.system?.toLowerCase().includes(lowerQuery) ||
             poi.properties.location?.toLowerCase().includes(lowerQuery) ||
             Object.values(poi.properties.metadata).some(value =>
               String(value).toLowerCase().includes(lowerQuery)
             );
    });
  }

  /**
   * Filter POIs by multiple criteria
   */
  filterPOIs(filters: {
    category?: POICategory;
    system?: string;
    location?: string;
    status?: string;
    visible?: boolean;
  }): POI[] {
    return Array.from(this.pois.values()).filter(poi => {
      if (filters.category && poi.category !== filters.category) return false;
      if (filters.system && poi.properties.system !== filters.system) return false;
      if (filters.location && !poi.properties.location?.includes(filters.location)) return false;
      if (filters.status && poi.properties.status !== filters.status) return false;
      if (filters.visible !== undefined && poi.visualization.visibility !== filters.visible) return false;

      return true;
    });
  }

  /**
   * Toggle POI visibility
   */
  togglePOIVisibility(poiId: string): void {
    const poi = this.pois.get(poiId);
    if (poi) {
      poi.visualization.visibility = !poi.visualization.visibility;
      this.emit('poi-updated', poi);
    }
  }

  /**
   * Set visibility for all POIs in a category
   */
  setCategoryVisibility(category: POICategory, visible: boolean): void {
    const poisInCategory = this.getPOIsByCategory(category);
    poisInCategory.forEach(poi => {
      poi.visualization.visibility = visible;
      this.emit('poi-updated', poi);
    });
  }

  /**
   * Enable or disable clustering
   */
  toggleClustering(): void {
    this.clusteringEnabled = !this.clusteringEnabled;
  }

  /**
   * Check if clustering is enabled
   */
  isClusteringEnabled(): boolean {
    return this.clusteringEnabled;
  }


  /**
   * Ensure POI has consistent coordinate representations
   */
  private ensureCoordinateConsistency(poi: POI): POI {
    const coords = poi.coordinates;

    // If we have lat/lng but no x/y, convert
    if (coords.latitude !== undefined && coords.longitude !== undefined &&
        (coords.x === undefined || coords.y === undefined)) {
      const modelCoords = this.coordinateTransformer.geographicToModel(
        { x: coords.longitude, y: coords.latitude, z: coords.z },
        this.modelOrigin
      );
      coords.x = modelCoords.x;
      coords.y = modelCoords.y;
    }

    // If we have x/y but no lat/lng, convert
    if (coords.x !== undefined && coords.y !== undefined &&
        (coords.latitude === undefined || coords.longitude === undefined)) {
      const geoCoords = this.coordinateTransformer.modelToGeographic(
        coords,
        this.modelOrigin
      );
      coords.latitude = geoCoords.latitude;
      coords.longitude = geoCoords.longitude;
    }

    return { ...poi, coordinates: coords };
  }

  /**
   * Import POIs from external data source
   */
  async importPOIs(data: any[]): Promise<void> {
    const importedPOIs: POI[] = [];

    for (const item of data) {
      try {
        const poi: POI = {
          id: item.id || `poi_${Date.now()}_${Math.random()}`,
          name: item.name || 'Unnamed POI',
          category: item.category || POICategory.UTILITY,
          coordinates: {
            x: item.x || 0,
            y: item.y || 0,
            z: item.z,
            latitude: item.latitude,
            longitude: item.longitude
          },
          properties: {
            description: item.description,
            type: item.type || 'generic',
            system: item.system,
            location: item.location,
            status: item.status || 'active',
            metadata: item.metadata || {}
          },
          visualization: {
            icon: item.icon || 'default',
            color: item.color || '#007bff',
            size: item.size || 12,
            visibility: item.visibility !== false
          }
        };

        importedPOIs.push(poi);
        this.addPOI(poi);
      } catch (error) {
        console.error('Failed to import POI:', item, error);
      }
    }

  }

  /**
   * Export POIs to JSON format
   */
  exportPOIs(): string {
    const poisArray = Array.from(this.pois.values());
    return JSON.stringify(poisArray, null, 2);
  }

  /**
   * Clear all POIs
   */
  clearAllPOIs(): void {
    this.pois.clear();
  }

  /**
   * Get POI statistics
   */
  getStatistics(): {
    total: number;
    byCategory: Record<POICategory, number>;
    visible: number;
    hidden: number;
  } {
    const pois = this.getAllPOIs();
    const stats = {
      total: pois.length,
      byCategory: {} as Record<POICategory, number>,
      visible: 0,
      hidden: 0
    };

    // Initialize category counts
    Object.values(POICategory).forEach(category => {
      stats.byCategory[category] = 0;
    });

    // Count POIs
    pois.forEach(poi => {
      stats.byCategory[poi.category]++;
      if (poi.visualization.visibility) {
        stats.visible++;
      } else {
        stats.hidden++;
      }
    });

    return stats;
  }

  /**
   * Get POIs by subcategory (IFC type name)
   */
  getPOIsBySubcategory(subcategory: string): POI[] {
    return Array.from(this.pois.values()).filter(poi =>
      poi.properties.type === subcategory ||
      poi.properties.subCategory === subcategory
    );
  }

  /**
   * Get visible POIs based on layer manager settings
   */
  getVisiblePOIs(): POI[] {
    const visiblePOIIds = poiLayerManager.getVisiblePOIs();
    return Array.from(this.pois.values()).filter(poi =>
      visiblePOIIds.includes(poi.id) && poi.visualization.visibility
    );
  }

  /**
   * Get POIs grouped by subcategory for layer management
   */
  getPOIsByLayerGroups(): POILayerGroup[] {
    return poiLayerManager.getLayerGroups();
  }

  /**
   * Update visibility for multiple POIs (called by layer manager)
   */
  private updatePOIsVisibility(poiIds: string[], visible: boolean): void {
    let updated = false;
    poiIds.forEach(poiId => {
      const poi = this.pois.get(poiId);
      if (poi && poi.visualization.visibility !== visible) {
        poi.visualization.visibility = visible;
        this.emit('poi-updated', poi);
        updated = true;
      }
    });

    if (updated) {
      this.emit('poi-layer-visibility-changed', { poiIds, visible });
    }
  }

  /**
   * Update visibility for all POIs (called by layer manager)
   */
  private updateAllPOIsVisibility(visible: boolean): void {
    const affectedPOIs: string[] = [];
    this.pois.forEach((poi, poiId) => {
      if (poi.visualization.visibility !== visible) {
        poi.visualization.visibility = visible;
        this.emit('poi-updated', poi);
        affectedPOIs.push(poiId);
      }
    });

    if (affectedPOIs.length > 0) {
      this.emit('poi-layer-visibility-changed', { poiIds: affectedPOIs, visible });
    }
  }

  /**
   * Sync POI data with layer manager (call after loading POIs from server)
   * Uses Systems page hierarchy: user-assigned systemCategory takes priority over IFC categories
   */
  public syncWithLayerManager(): void {
    // Priority: user-assigned systemCategory > IFC originalCategory (matches Systems page exactly)
    const poiData = Array.from(this.pois.values()).map(poi => {
      // Determine effective category using Systems page hierarchy
      const effectiveCategory = poi.properties.systemCategory || poi.properties.originalCategory || 'Structure';
      const effectiveSubcategory = poi.properties.systemSubcategory || poi.properties.subCategory || poi.properties.type;

      return {
        id: poi.id,
        title: poi.name,
        poi_group_id: effectiveCategory.toLowerCase().replace(/\s+/g, '-'),
        poi_group_name: effectiveCategory,
        poi_type_id: effectiveSubcategory,
        poi_type_name: effectiveSubcategory,
        poi_type_icon: poi.visualization.icon,
        ifc_category: poi.properties.originalCategory || poi.properties.type,
        // User-assigned system hierarchy (takes priority in POILayerManager)
        system_category: poi.properties.systemCategory,
        system_subcategory: poi.properties.systemSubcategory,
        system_name: poi.properties.systemName,
        visibility_check: poi.visualization.visibility
      };
    });

    poiLayerManager.updatePOIData(poiData);
  }

  /**
   * Get POI layer visibility state for a specific POI
   */
  public isPOILayerVisible(poiId: string): boolean {
    return poiLayerManager.getPOIVisibility(poiId);
  }

  /**
   * Filter POIs by layer visibility and other criteria
   */
  public getFilteredPOIs(filters: {
    category?: POICategory;
    subcategory?: string;
    system?: string;
    location?: string;
    status?: string;
    visible?: boolean;
    respectLayerVisibility?: boolean;
  } = {}): POI[] {
    let pois = Array.from(this.pois.values());

    // Apply layer visibility filter first if requested
    if (filters.respectLayerVisibility !== false) {
      const visiblePOIIds = poiLayerManager.getVisiblePOIs();
      pois = pois.filter(poi => visiblePOIIds.includes(poi.id));
    }

    // Apply other filters
    return pois.filter(poi => {
      if (filters.category && poi.category !== filters.category) return false;
      if (filters.subcategory && poi.properties.subCategory !== filters.subcategory) return false;
      if (filters.system && poi.properties.system !== filters.system) return false;
      if (filters.location && !poi.properties.location?.includes(filters.location)) return false;
      if (filters.status && poi.properties.status !== filters.status) return false;
      if (filters.visible !== undefined && poi.visualization.visibility !== filters.visible) return false;

      return true;
    });
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): POIService {
    if (!POIService.instance) {
      POIService.instance = new POIService();
    }
    return POIService.instance;
  }
}

// Export singleton instance
export const poiService = POIService.getInstance();